import {create, StateCreator, useStore} from 'zustand';
import {devtools} from 'zustand/middleware';
import {temporal, TemporalState} from 'zundo';
import isEqual from 'lodash/isEqual';

import {
  ExperimentMetric,
  ExperimentMetricResults,
  ExperimentMetricType,
  ExperimentUpdates,
} from 'apps/experiments/src/types';
import {FeatureFlagUpdates} from 'apps/feature-flags/src/store';
import {SegmentFilterController} from 'common/charts/contexts/segment-filter-controller';

export type MetricGroups = Record<ExperimentMetricType, ExperimentMetric[]>;
export type MetricResult = Record<string, ExperimentMetricResults> | Error;
export type MetricResults = Record<string, MetricResult>;

type ExperimentAppState = {
  draftExperiment?: ExperimentUpdates;
  draftFeatureFlag?: FeatureFlagUpdates;
  currentMetricId?: string;
  metricsSearch?: string;
  segmentFilterController?: SegmentFilterController;
  allowNavigatingAway: boolean;
  configurationGuideOpen: boolean;
};

type ExperimentAppStore = ExperimentAppState & {
  setDraftExperiment: (experiment?: ExperimentUpdates) => void;
  setDraftFeatureFlag: (featureFlag?: FeatureFlagUpdates) => void;
  setCurrentMetricId: (currentMetricId?: string) => void;
  setMetricsSearch: (search?: string) => void;
  setSegmentFilterController: (segmentFilterController: SegmentFilterController) => void;
  setAllowNavigatingAway: (allow: boolean) => void;
  setConfigurationGuideOpen: (open: boolean) => void;
  reset: () => void;
};

type PartializedExperimentAppStore = Pick<ExperimentAppStore, `draftExperiment`>;

const initialState: ExperimentAppState = {
  draftExperiment: undefined,
  draftFeatureFlag: undefined,
  currentMetricId: undefined,
  metricsSearch: undefined,
  segmentFilterController: new SegmentFilterController(),
  allowNavigatingAway: false,
  configurationGuideOpen: false,
};

const createAppSlice: StateCreator<ExperimentAppStore, [], [], ExperimentAppStore> = (set) => ({
  ...initialState,
  setDraftExperiment: (draftExperiment) => set({draftExperiment}),
  setDraftFeatureFlag: (draftFeatureFlag) => set({draftFeatureFlag}),
  setCurrentMetricId: (currentMetricId) => set({currentMetricId}),
  setMetricsSearch: (metricsSearch) => set({metricsSearch}),
  setSegmentFilterController: (segmentFilterController) => set({segmentFilterController}),
  setAllowNavigatingAway: (allowNavigatingAway) => set({allowNavigatingAway}),
  setConfigurationGuideOpen: (configurationGuideOpen) => set({configurationGuideOpen}),
  reset: () => set({...initialState, segmentFilterController: new SegmentFilterController()}),
});

export const useExperimentsStore = create<ExperimentAppStore>()(
  devtools(
    temporal(
      (...a) => ({
        ...createAppSlice(...a),
      }),
      {
        partialize: (state) => ({
          draftExperiment: state.draftExperiment,
          draftFeatureFlag: state.draftFeatureFlag,
        }),
        equality: (pastState, currentState) => isEqual(pastState, currentState),
      },
    ),
  ),
);

export const useTemporalStore = <T>(
  // Use partialized StoreState type as the generic here
  selector: (state: TemporalState<PartializedExperimentAppStore>) => T,
) => useStore(useExperimentsStore.temporal, selector);
